/**
 * The persistent frame (M1.1, docs/11-interface.md): the overlays summoned
 * from anywhere over the live floor on the left, cash in the middle, and on
 * the right the one speed dial for both clocks, the calendar, and the settings
 * gear (saves, the floor's panels, the debug HUD).
 *
 * The speed is *sim state*: the buttons send commands and read the store back,
 * so the bar can never show a speed the worker is not running. One ladder
 * drives both clocks — a watched speed multiplies theater time and the
 * calendar closes a week every floor *day* at that pace (the fixed 7:1
 * coupling: one calendar week is one theater day on the floor); `▶▶` is the
 * fast-forward where the floor stands still and the calendar runs on the
 * line's curve (docs/02-world.md — the curves carry the load).
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { FloorHost } from '../bridge';
import { snapshot, subscribe } from './store';
import { setPanels, toggleEconomy, usePanels } from './panels';
import { Tip } from './Tooltip';
import type { SaveStore } from './saves';
import { money } from './format';
import { downloadPlaytestLog } from './playtest';

/** What the shell can do about saves; the bar only presses the buttons. */
export interface CampaignActions {
  saveNow: () => Promise<void>;
  exportSave: () => Promise<void>;
  importSave: () => Promise<void>;
  newCampaign: () => Promise<void>;
  /** Where the last autosave went, and when (wall clock), for the bar's note. */
  autosave: () => { store: SaveStore | null; at: number | null; error: string | null };
}

/** Watched speeds, as multiples of theater time (1× = 30 theater s a second). */
const SPEEDS = [1, 2, 4, 8, 64];
const FORWARD_RATES = [1, 4, 12, 52];

export function TopBar({ host, actions }: { host: FloorHost; actions: CampaignActions }) {
  const state = useSyncExternalStore(subscribe, snapshot);
  const panels = usePanels();
  const c = state.campaign;
  const ui = state.ui;
  if (!c) {
    return (
      <div className="topbar">
        <span className="topbar-title">YIELD</span>
        <span className="muted">booting the campaign…</span>
      </div>
    );
  }
  const ended = c.run !== 'running';
  const runLabel =
    c.run === 'ended:insolvency' ? 'insolvent — the run is over' : c.run === 'ended:window' ? 'the window closed' : null;
  const watch = (speed: number) => {
    host.setPace('watch');
    host.send({ kind: 'setSpeed', mille: speed * 1000 });
  };
  return (
    <div className="topbar">
      <span className="topbar-title">YIELD</span>
      <span className="topbar-panels">
        <button className={panels.economy === 'dashboard' ? 'active' : ''} onClick={() => toggleEconomy('dashboard')}>
          Dashboard
        </button>
        <button className={panels.economy === 'technology' ? 'active' : ''} onClick={() => toggleEconomy('technology')}>
          Technology
        </button>
        <button className={panels.economy === 'markets' ? 'active' : ''} onClick={() => toggleEconomy('markets')}>
          Markets
        </button>
        <button
          className={panels.handbook ? 'active' : ''}
          onClick={() => setPanels({ handbook: panels.handbook ? null : 'contents' })}
          title="the handbook: one page per system"
        >
          ? Handbook
        </button>
      </span>
      <span className="topbar-figures">
        <span className="topbar-figure">
          <span className="topbar-figure-label">Cash</span>
          <Tip figure={c.firm.cashFigure}>
            <span className={`topbar-cash-value ${c.firm.cash < 0 ? 'over' : ''}`}>{c.firm.cashFigure.value}</span>
          </Tip>
          {c.firm.debt > 0 && <span className="muted"> · debt {money(c.firm.debt)}</span>}
        </span>
        <span className="topbar-figure" title="last week's result: income minus every cost, before the bank">
          <span className="topbar-figure-label">Profit</span>
          <Tip figure={c.firm.ledger.swingFigure}>
            <span className={`topbar-cash-value ${c.firm.ledger.swing < 0 ? 'over' : c.firm.ledger.swing > 0 ? 'ok' : ''}`}>
              {c.firm.ledger.swing > 0 ? '+' : ''}
              {c.firm.ledger.swingFigure.value}
            </span>
          </Tip>
        </span>
        <span className="topbar-figure" title="last week's revenue: base income plus what the markets paid">
          <span className="topbar-figure-label">Revenue</span>
          <Tip figure={c.firm.ledger.revenueFigure}>
            <span className="topbar-cash-value">{c.firm.ledger.revenueFigure.value}</span>
          </Tip>
        </span>
      </span>

      <span className="topbar-right">
        {ended ? (
          <span className="chip over">{runLabel}</span>
        ) : (
          <span className="topbar-pace">
            <button className={c.pace === 'paused' ? 'active' : ''} onClick={() => host.setPace('paused')} title="pause both clocks">
              ▮▮
            </button>
            {SPEEDS.map((speed) => (
              <button
                key={speed}
                className={c.pace === 'watch' && ui?.speedMille === speed * 1000 ? 'active' : ''}
                onClick={() => watch(speed)}
                title={`watch at ${speed}× theater time — the floor runs, and a calendar week closes every floor day (${Math.round(48 / speed)} min of watching)`}
              >
                {speed}×
              </button>
            ))}
            <button
              className={c.pace === 'forward' ? 'active' : ''}
              onClick={() => host.setPace('forward')}
              title="fast-forward: the floor stands still and the calendar runs on the line's curve"
            >
              ▶▶
            </button>
            {c.pace === 'forward' &&
              FORWARD_RATES.map((rate) => (
                <button
                  key={rate}
                  className={`small ${c.forwardWeeksPerSecond === rate ? 'active' : ''}`}
                  onClick={() => host.setForwardRate(rate)}
                  title={`${rate} week${rate > 1 ? 's' : ''} a second`}
                >
                  {rate}w/s
                </button>
              ))}
          </span>
        )}
        <span className="topbar-date" title={`week ${c.week} of ${c.windowWeeks} · ${c.pace} · the clock is the floor's day, one per calendar week`}>
          <span>
            {c.date} <span className="topbar-clock">{c.clock}</span>
          </span>
          {c.pace === 'watch' && !ended && (
            <span className="topbar-week">
              <span className="topbar-week-bar" style={{ width: `${c.weekProgressMille / 10}%` }} />
            </span>
          )}
        </span>
        <Settings host={host} actions={actions} />
      </span>
    </div>
  );
}

/** The gear: saves, the layout files, which panels are shown, the debug HUD. */
function Settings({ host, actions }: { host: FloorHost; actions: CampaignActions }) {
  const panels = usePanels();
  const catalogId = useSyncExternalStore(subscribe, snapshot).ui?.site.catalogId ?? 'site-1974';
  const otherEra = catalogId === 'site-1974' ? 'site-1950' : 'site-1974';
  const fileInput = useRef<HTMLInputElement>(null);
  const autosave = actions.autosave();
  const menu = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!panels.settings) return;
    const onClick = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) setPanels({ settings: false });
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPanels({ settings: false });
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [panels.settings]);
  return (
    <span ref={menu} className="topbar-settings">
      <button
        className={`gear ${panels.settings ? 'active' : ''}`}
        onClick={() => setPanels({ settings: !panels.settings })}
        title="settings"
        aria-label="settings"
      >
        ⚙
      </button>
      {panels.settings && (
        <div className="settings-menu">
          <div className="settings-group">
            <span className="settings-label">campaign</span>
            <button onClick={() => void actions.saveNow()} title="autosave now (OPFS, or IndexedDB where OPFS cannot write)">
              save now
            </button>
            <button onClick={() => void actions.exportSave()} title="export the campaign as a file you own">
              export to file
            </button>
            <button onClick={() => void actions.importSave()} title="import a campaign file">
              import from file
            </button>
            <button onClick={() => void actions.newCampaign()} title="start over — the autosave is cleared">
              new campaign
            </button>
            <button onClick={() => downloadPlaytestLog()} title="the sitting's own log (B0.7): time on site, edits, levers, progression — playtest evidence">
              save the sitting log
            </button>
            <span className="muted settings-note" title={autosave.error ?? undefined}>
              {autosave.error
                ? `autosave failed — ${autosave.error}`
                : autosave.at
                  ? `autosaved ${ago(autosave.at)} · ${autosave.store}`
                  : 'not saved yet'}
            </span>
          </div>
          <div className="settings-group">
            <span className="settings-label">the floor's layout</span>
            <button onClick={() => void exportLayout(host)} title="the site's layout as a file (yield.site.layout/1)">
              export layout to file
            </button>
            <button onClick={() => fileInput.current?.click()} title="import a layout file — it goes through the same rules a click does">
              import layout from file
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void file.text().then((json) => host.send({ kind: 'loadLayout', json }));
                event.target.value = '';
              }}
            />
            <button onClick={() => host.send({ kind: 'resetSite', catalog: catalogId })} title="clear the plot back to empty on this catalog">
              clear the plot
            </button>
            <button
              onClick={() => host.send({ kind: 'resetSite', catalog: otherEra })}
              title="the other era's catalog, as the schema's smoke test made clickable (the palette still lists the boot catalog)"
            >
              empty {otherEra === 'site-1950' ? '1950' : '1974'} plot
            </button>
          </div>
          <div className="settings-group">
            <span className="settings-label">panels</span>
            <label>
              <input
                type="checkbox"
                checked={panels.floorPanels}
                onChange={(e) => setPanels({ floorPanels: e.target.checked })}
              />{' '}
              the floor's panels (site &amp; flow, the build dock)
            </label>
            <label>
              <input type="checkbox" checked={panels.debug} onChange={(e) => setPanels({ debug: e.target.checked })} />{' '}
              debug HUD (frame, boundary, worker; B0's raw controls)
            </label>
          </div>
        </div>
      )}
    </span>
  );
}

/** Export: ask the sim for the layout, hand the browser a file. */
async function exportLayout(host: FloorHost): Promise<void> {
  const json = await host.exportLayout();
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'yield-layout.json';
  link.click();
  URL.revokeObjectURL(url);
}

function ago(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}
